import React, {useEffect, useRef} from 'react'
import {Stage, Layer} from 'react-konva'
import {VIEW_MODES} from '../../constants/editorConfig'

/**
 * Host for Konva Stage + two layers (overlay, silhouette).
 * Only responsibility: mount Stage, attach canvas to runtime, lifecycle (mount/unmount).
 * Does not handle fragment detection or UI overlays.
 *
 * @param {{
 *   runtime: object
 *   width: number
 *   height: number
 *   viewMode: string
 *   children?: React.ReactNode  - rendered inside overlay layer (e.g. SelectionTransformer)
 *   highlightLayer?: React.ReactNode  - optional Konva Layer(s) rendered inside Stage (e.g. FragmentHighlight)
 *   onReady?: (refs: { stageRef: React.RefObject, layerRefOverlay: React.RefObject, layerRefSilhouette: React.RefObject }) => void
 * }} props
 */
const CanvasHost = ({
    runtime,
    width = 0,
    height = 0,
    viewMode = VIEW_MODES.OVERLAY,
    children,
    highlightLayer,
    onReady,
}) => {
    const stageRef = useRef(null)
    const layerRefOverlay = useRef(null)
    const layerRefSilhouette = useRef(null)

    useEffect(() => {
        if (!stageRef.current || !layerRefOverlay.current || !layerRefSilhouette.current) return
        runtime.attachCanvas({
            stage: stageRef.current,
            layers: {
                [VIEW_MODES.OVERLAY]: layerRefOverlay.current,
                [VIEW_MODES.SILHOUETTE]: layerRefSilhouette.current,
            },
        })
        runtime.mount()
        runtime.setViewMode(viewMode)
        if (onReady) {
            onReady({stageRef, layerRefOverlay, layerRefSilhouette})
        }
        return () => {
            runtime.unmount()
        }
    }, [])

    useEffect(() => {
        runtime.setViewMode(viewMode)
    }, [viewMode, runtime])

    return (
        <div style={{position: 'relative', width: '100%', height: '100%'}}>
            <Stage ref={stageRef} width={width} height={height}>
                <Layer ref={layerRefOverlay}>{children}</Layer>
                <Layer ref={layerRefSilhouette} listening={false} />
                {highlightLayer}
            </Stage>
        </div>
    )
}

export default CanvasHost
